import React, { Fragment } from 'react';
import PropTypes from 'prop-types';

import { cartItemsArrayPropTypes } from 'helpers/propTypes';
import QuantitySelector from 'components/commons/QuantitySelector';
import Button from 'components/commons/Button';
import Benefit from 'components/commons/Benefit';
import Loading from 'components/commons/Loading';
import Breadcrumb from 'components/commons/Breadcrumb';
import DescriptionItem from 'components/commons/DescriptionItem';

import './CartTextil.scss';
import { getTotalPriceFromQuantity, getUnitPriceFromQuantity, getTextilQuantityAndTotal } from 'helpers/prices';

const CartTextil = ({ cartSize, items, minimumPurchase, onQuantityChange, onRemoveFromCart, subtotal, priceDiscount, onItemClick, loading }) => {
  const showCart = cartSize > 0;
  
  const total = subtotal - priceDiscount;

  const itemsUseMetros = items.filter(({ item }) => item.usoMetros);
  const itemsNotUseMetros = items.filter(({ item }) => !item.usoMetros);

  const renderEmptyCart = () => (
    <h2 className="text-center">El carrito está vacío</h2>
  );

  const findProductWholesale = (id, item) => {
    const product = item.product_wholesaler.find((prod) => prod.id === id);
    return product.price * product.quantity;
  };

  const findIDProductWholesale = (subItem) => {
    const values = Object.values(subItem);
    const firstArray = values[0];
    const firstObject = firstArray[0];
    const { id } = firstObject.wholesale;
    if (id > 0) return `Precio por ${firstObject.wholesale.quantity}`;
    return 'Precio Mayorista';
  };

  const findPriceProductWholesale = (subItem, item) => {
    const values = Object.values(subItem);
    const firstArray = values[0];
    const firstObject = firstArray[0];
    const { id } = firstObject.wholesale;
    if (id > 0) {
      const product = item.product_wholesaler.find((prod) => prod.id === id);
      return product.price * product.quantity;
    }
    return item.sale ? item.sale : item.price;
  };

  const renderDescriptions = (descriptions, colors, item, descriptionId) => {
    const color = colors.find(
      ({ stock_description_id }) => stock_description_id === Number(descriptionId),
    );
    const combinations = descriptions;
    return !!combinations.length && (
      <div className="cart-item-description-option" key={`item-${item.id}-description-${descriptionId}`}>
        {combinations.map((combination, index) => (
          <Fragment key={`${item.id}-${combination.description.id}-${combination.wholesale.id}`}>
            {index === 0 && (
              <div className="clearfix">
                <DescriptionItem
                  description={color}
                  onQuantityChange={(value, desc) => {
                    onQuantityChange(item, value, desc, combination.wholesale);
                  }}
                  showQuantitySelector={combination.description.id === color.id}
                  storage={{ quantity: combination.quantity }}
                />
              </div>
            )}
            {combination.description.id !== color.id && (
              <div
                className="float-xs-left"
                key={`${item.id}-${combination.description.id}-${combination.wholesale.id}`}
              >
                <DescriptionItem
                  description={combination.description}
                  onQuantityChange={(value, desc) => {
                    onQuantityChange(item, value, desc, combination.wholesale);
                  }}
                  storage={{ quantity: combination.quantity }}
                  othersWholesale={combination.othersSubItemsWholesale}
                />
              </div>
            )}
          </Fragment>
        ))}
      </div>
    );
  };

  const renderPriceHasWholesale = (subItemDescription) => {
    for (const key in subItemDescription) {
      if (Object.prototype.hasOwnProperty.call(subItemDescription, key)) {
        const entries = subItemDescription[key];
        for (const entry of entries) {
          if (entry.wholesale && entry.wholesale.bool) return true;
        }
      }
    }
    return false;
  };

  const renderPriceDescriptions = (talles, price) => {
    const uniqueArray = talles.reduce((accumulator, currentObj) => {
      const exists = accumulator.find(
        (obj) =>
          obj.description.stock_description_id === currentObj.description.stock_description_id,
      );
      if (!exists) accumulator.push(currentObj);
      return accumulator;
    }, []);

    return (
      <div className="precios-talles">
        {uniqueArray.map((item, index) => {
          const stockDescription = item.description.stock_descriptions;
          return (
            <React.Fragment key={index}>
              {stockDescription?.name}: $
              {item.description.price && item.description.price > 0
                ? item.description.price
                : price}
              {index < uniqueArray.length - 1 ? ', ' : ''}
            </React.Fragment>
          );
        })}
      </div>
    );
  };

  const vals = (obj) => Object.values(obj || {});
  const keys = (obj) => Object.keys(obj || {});
  
  const renderFullCart = () => (
    <div className="cart">
      <>
        <h2 className="text-center">
          {`Hay ${cartSize} producto${cartSize > 1 ? 's' : ''}`}
        </h2>
        <div className="cart-items">
          <div className="separator" />
          {itemsUseMetros.map(({ item, quantity }) => {
            const { quantity: totalQuantity, total: itemTotal } = getTextilQuantityAndTotal(item, quantity);
            return (!!item && 
            <div className="cart-item" key={`cart-item-metros-${item.id}`}>
              <div className="d-flex">
                <Button
                  text={<i className="fa fa-remove" />}
                  buttonClass="cart-item-remove"
                  onClick={() => onRemoveFromCart(item)}
                />
              </div>
              <div className="row">
                <div className="col-md-4 d-flex align-items-center justify-content-center">
                  <img
                    className="cursor-pointer"
                    onClick={() => onItemClick(item)}
                    src={item.image1}
                    alt={item.name}
                  />
                </div>
                <div className="col-md-8">
                  <ul className="cart-item-info">
                    <li className="d-flex flex-row">
                      <span className="cart-item-title cursor-pointer" onClick={() => onItemClick(item)}>
                        {item.name}
                      </span>
                    </li>
                    <li className="cart-item-description">{item.description}</li>
                    <li>
                      <span className="cart-item-price">
                        {/* ${item.price.toLocaleString('de-DE')} */}
                        {itemTotal.toLocaleString('de-DE')}{' '}
                      </span>
                    </li>
                    <li>
                      Metros: {totalQuantity}
                    </li>
                  </ul>
                </div>
              </div>
              <div className="separator" />
            </div>
          )})}

          {itemsNotUseMetros.map(
            ({ item, quantity, colors, subItems, subItemsDescriptions, talles }) =>
              !!item && (
                <div className="cart-item" key={`cart-item-${item.id}`}>
                  <div className="d-flex">
                    <Button
                      text={<i className="fa fa-remove" />}
                      buttonClass="cart-item-remove"
                      onClick={() => onRemoveFromCart(item)}
                    />
                  </div>
                  <div className="row">
                    <div className="col-md-4 d-flex align-items-center justify-content-center">
                      <img
                        className="cursor-pointer"
                        onClick={() => onItemClick(item)}
                        src={item.image1}
                        alt={item.name}
                        role="presentation"
                      />
                    </div>
                    <div className="col-md-8">
                      <ul className="cart-item-info">
                        <li className="d-flex flex-row">
                          <span
                            className="cart-item-title cursor-pointer"
                            onClick={() => onItemClick(item)}
                            role="presentation"
                          >
                            {item.name}
                          </span>
                        </li>
                        <li>
                          <span className="cart-item-description">{item.description}</span>
                        </li>

                        {/* Caso simple: sin mayorista ni descripciones -> precio base + selector */}
                        <li>
                          {!(item.product_wholesaler.length > 0) &&
                            item.stock_product_descriptions.length === 0 && (
                              <>
                                <span className="cart-item-price">
                                  {`$${(item.sale ? item.sale : item.price).toLocaleString('de-DE')}`}
                                  {!!item.sale && (
                                    <span>{`$${item.price.toLocaleString('de-DE')}`}</span>
                                  )}
                                </span>
                                <QuantitySelector
                                  limit={item.stock}
                                  onQuantityChange={(value) =>
                                    onQuantityChange(item, value, null, {
                                      id: 0,
                                      bool: false,
                                      quantity: 0,
                                    })
                                  }
                                  quantity={quantity}
                                />
                              </>
                            )}
                        </li>

                        {/* Con descripciones/colores/talles, etc. */}
                        <li className="descriptions-col">
                        {colors && subItemsDescriptions && (
                          <div className="descriptions-container">
                            {vals(subItemsDescriptions).some(
                              (v) => v && typeof v === 'object' && keys(v).length > 0
                            ) && (
                                <div className="precios-container-wrapper">
                                  {Object.values(subItemsDescriptions).map((subItemDescription, idx) => (
                                    <Fragment key={`${item.id}-subdesc-${idx}`}>
                                      {Object.keys(subItemDescription).length > 0 && (
                                        <div className="precios-container">
                                          <span className="cart-item-price">
                                            {!renderPriceHasWholesale(subItemDescription) ? (
                                              <>
                                                Precio por talle
                                                {renderPriceDescriptions(
                                                  Object.values(subItemDescription).flatMap((desc) =>
                                                    Array.isArray(desc)
                                                      ? desc.filter(
                                                          (d) =>
                                                            d.description.stock_descriptions
                                                              .stock_category_id === 2,
                                                        )
                                                      : desc.description.stock_descriptions.stock_category_id === 2
                                                      ? [desc]
                                                      : [],
                                                  ),
                                                  item.price,
                                                )}
                                              </>
                                            ) : (
                                              <>
                                                {!!item.sale && item.product_wholesaler.length === 0 && (
                                                  <span>{`$${item.price.toLocaleString('de-DE')}`}</span>
                                                )}
                                                {`$${findPriceProductWholesale(
                                                  subItemDescription,
                                                  item,
                                                ).toLocaleString('de-DE')}`}
                                              </>
                                            )}
                                          </span>
                                          <span className="title-description search-item">
                                            {findIDProductWholesale(subItemDescription)}
                                          </span>
                                          {Object.entries(subItemDescription).map(
                                            ([descriptionId, descriptionArray]) =>
                                              renderDescriptions(
                                                descriptionArray,
                                                colors,
                                                item,
                                                descriptionId,
                                              ),
                                          )}
                                        </div>
                                      )}
                                    </Fragment>
                                  ))}
                                </div>
                              )}
                            </div>
                          )}

                          {/* Bloque mayorista “simple” (sin descripciones) */}
                          {subItems?.length > 0 && (
                            <div className="precios-container-wrapper">
                              {subItems.map((subItem) => (
                                <div className="precios-container" key={`${item.id}-wh-${subItem.wholesale.id}`}>
                                  <span className="cart-item-price">
                                    {`$${(
                                      subItem.wholesale.id > 0
                                        ? findProductWholesale(subItem.wholesale.id, item)
                                        : item.price
                                    ).toLocaleString('de-DE')}`}
                                  </span>
                                  <span className="title-description search-item">
                                    {subItem.wholesale.id > 0
                                      ? `Precio por ${subItem.wholesale.quantity}`
                                      : 'Precio Mayorista'}
                                  </span>
                                  <QuantitySelector
                                    limit={item.stock}
                                    onQuantityChange={(value) =>
                                      onQuantityChange(item, value, null, subItem.wholesale)
                                    }
                                    quantity={subItem.quantity}
                                    quantityOthersWholesale={subItems
                                      .filter(({ wholesale }) => wholesale.id !== subItem.wholesale.id)
                                      .map((other) => other.quantity)}
                                  />
                                </div>
                              ))}
                            </div>
                          )}
                        </li>
                      </ul>
                    </div>
                  </div>
                  <div className="separator" />
                </div>
              ),
          )}
        </div>
      </>
    </div>
  );

  const renderCheckoutDetails = () => (
    <div className="row">
      <div className="col">
        <div className="checkout">
          <ul className="checkout-details">
            <li>
              Subtotal:
              <span className="float-right">
                {/* ${subtotal.toLocaleString('de-DE')} */}
                {total.toLocaleString('de-DE')}
              </span>
            </li>
            {priceDiscount != null && (
              <li>
                Con descuento:
                <span className="float-right">
                  ${priceDiscount.toLocaleString('de-DE')}
                </span>
              </li>
            )}
          </ul>
          <div className="row">
            <div className="col-lg-6">
              <Button text="Envío a domicilio" buttonClass="checkout-button" href="/envio-a-domicilio" />
            </div>
            <div className="col-lg-6">
              <Button text="Retiro en Sucursal" buttonClass="checkout-button" href="/retiro-en-sucursal" />
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div className="container">
      <div className="row">
        <div className="col">
          <Breadcrumb urls={['/carrito']} names={['Carrito']} />
          {!loading ? (
            (!showCart ? (
              renderEmptyCart()
            ) : (
              <>
                {renderFullCart()}
                {renderCheckoutDetails()}
              </>
            ))
          ) : <Loading />}
        </div>
      </div>
      <div className="section">
        <Benefit minimumPurchase={minimumPurchase} />
      </div>
    </div>
  );
};

CartTextil.defaultProps = {
  priceDiscount: null,
};

CartTextil.propTypes = {
  cartSize: PropTypes.number.isRequired,
  items: cartItemsArrayPropTypes.isRequired,
  minimumPurchase: PropTypes.number.isRequired,
  onQuantityChange: PropTypes.func.isRequired,
  onRemoveFromCart: PropTypes.func.isRequired,
  subtotal: PropTypes.number.isRequired,
  onItemClick: PropTypes.func.isRequired,
  priceDiscount: PropTypes.number,
  loading: PropTypes.bool.isRequired,
};

export default CartTextil;
